import React, { useContext } from 'react';
import { isEmpty } from 'lodash';
import { FormattedMessage } from 'react-intl';
import {
  Banner,
  DisplayText,
  Heading,
  RadioButton,
  PositiveNumberField,
  TextStyle
} from '@superops/ui-design-system';
import { Column, CurrencyField, CurrencyText } from '@nile/components';

import { ServiceCatalogItem } from '../../types';
import { ServiceCatalogFormContext } from '../index';
import { ServiceItemsDropdown, SelectedServiceItems } from '../../Components';

function BlockHourTypeItem({
  removeBundledItem,
  handleBundledItems,
  selectedCatalogItem,
  handlePropertyChange,
  disableServiceCatalogItemCostAndPrice
}: {
  handleBundledItems: (item) => void;
  removeBundledItem: (itemId) => void;
  selectedCatalogItem: ServiceCatalogItem;
  handlePropertyChange: (key, val) => void;
  disableServiceCatalogItemCostAndPrice: boolean;
}): React.ReactElement {
  const { errors, currencyValue, currencyCode } = useContext(
    ServiceCatalogFormContext
  );

  function handleChange(key) {
    return (value) =>
      handlePropertyChange('serviceTypeItem', {
        ...selectedCatalogItem?.serviceTypeItem,
        redeemableServiceBundle: {
          ...selectedCatalogItem?.serviceTypeItem?.redeemableServiceBundle,
          [key]: value
        }
      });
  }

  function handleHourlyRateChange(value) {
    handlePropertyChange(
      'sellingPrice',
      isEmpty(value)
        ? null
        : {
            ...selectedCatalogItem?.sellingPrice,
            model: 'PER_UNIT',
            details: [
              {
                ...selectedCatalogItem?.sellingPrice?.details[0],
                value: value
              }
            ]
          }
    );
  }

  let totalPrice = 0;
  if (
    selectedCatalogItem?.serviceTypeItem?.redeemableServiceBundle
      ?.redeemableQuantity &&
    selectedCatalogItem?.sellingPrice?.details?.[0]?.value
  ) {
    totalPrice =
      parseFloat(
        selectedCatalogItem.serviceTypeItem.redeemableServiceBundle
          .redeemableQuantity
      ) * parseFloat(selectedCatalogItem.sellingPrice.details[0].value);
  }

  return (
    <div className="grid g20">
      <PositiveNumberField
        size="small"
        type="number"
        label={<FormattedMessage id="common.quantity" />}
        value={selectedCatalogItem?.serviceTypeItem?.redeemableServiceBundle?.redeemableQuantity?.toString()}
        placeholder="0"
        suffix="Hours"
        autoComplete="off"
        onChange={handleChange('redeemableQuantity')}
        error={
          errors?.[
            'serviceTypeItem.redeemableServiceBundle.redeemableQuantity'
          ]?.[0] && <FormattedMessage id="Quantity can't be blank" />
        }
        disabled={disableServiceCatalogItemCostAndPrice}
      />
      <div className="fx aend g20">
        <CurrencyField
          type="number"
          label={
            <FormattedMessage id="admin.serviceCatalog.item.blockHour.hourly.rate" />
          }
          size="small"
          placeholder="0"
          // suffix={`${currencyValue}${currencyCode}`}
          value={selectedCatalogItem?.sellingPrice?.details[0]?.value}
          autoComplete="off"
          onChange={handleHourlyRateChange}
          disabled={disableServiceCatalogItemCostAndPrice}
          showCurrency
          currencyInPrefix={false}
        />
        {totalPrice ? (
          <Banner status="info" hideIcon>
            <FormattedMessage
              id="admin.serviceCatalog.total.price"
              defaultMessage="Total price will be"
            />{' '}
            <TextStyle weight="SemiBold">
              <CurrencyText
                value={
                  totalPrice.toString()?.split('.')?.[1]?.length > 3
                    ? totalPrice.toFixed(3)
                    : totalPrice
                }
              />
            </TextStyle>
          </Banner>
        ) : null}
      </div>
      <Heading element="h6">
        <FormattedMessage id="admin.serviceCatalog.applicableIn.placeholder" />
      </Heading>
      <div className="fx actr g10">
        <RadioButton
          checked={
            selectedCatalogItem?.serviceTypeItem?.redeemableServiceBundle
              ?.applicableHours === 'ALL_HOURS'
          }
          label={
            <FormattedMessage id="admin.serviceCatalog.blockHourTypes.allHours" />
          }
          id="ALL_HOURS"
          onChange={(checked, id) =>
            checked && handleChange('applicableHours')(id)
          }
          disabled={disableServiceCatalogItemCostAndPrice}
        />
        <RadioButton
          checked={
            selectedCatalogItem?.serviceTypeItem?.redeemableServiceBundle
              ?.applicableHours === 'BUSINESS_HOURS'
          }
          label={
            <FormattedMessage id="admin.serviceCatalog.blockHourTypes.businessHours" />
          }
          id="BUSINESS_HOURS"
          onChange={(checked, id) =>
            checked && handleChange('applicableHours')(id)
          }
          disabled={disableServiceCatalogItemCostAndPrice}
        />
      </div>
      <Column gap={10} className="mt-20">
        <Heading element="h6">
          <FormattedMessage id="Usage" />
        </Heading>

        <div className="fx actr g5">
          <FormattedMessage id="admin.serviceCatalog.offeringType.blockHour.included.items" />
          <ServiceItemsDropdown
            offerItemType="BLOCK_HOUR"
            onChange={handleBundledItems}
            filterServiceItems={
              selectedCatalogItem?.serviceTypeItem?.bundledItems || []
            }
            isDisabled={disableServiceCatalogItemCostAndPrice}
          />
        </div>
      </Column>
      <SelectedServiceItems
        selectedServiceItems={
          selectedCatalogItem?.serviceTypeItem?.bundledItems
        }
        onDeleteHandler={removeBundledItem}
        isDisabled={disableServiceCatalogItemCostAndPrice}
      />
    </div>
  );
}

export default BlockHourTypeItem;
